import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { startGlobalTimer, stopAllTimers, setGlobalTimer } from "store/slices/timer";
import { setSavedExerciseAssignments, getAssignments } from "store/slices/exercise";
import { getUserEmail } from "store/slices/user";

import { getSessionFromStorage } from "utils/sessionStorageHandler";

import Test from "./02_Test/Test";
import Summary from "./03_Summary/Summary";
import Results from "./04_Results/Results";
import { useTrainingTabLock } from "./useTrainingTabLock";

type TExerciseStep = "test" | "summary" | "results";

const Exercise = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const dispatch = useAppDispatch();
  const assignments = useAppSelector(getAssignments);
  const userEmail = useAppSelector(getUserEmail);

  const [step, setStep] = useState<TExerciseStep | undefined>(undefined);
  const isLockedByOtherTab = useTrainingTabLock(sessionId, userEmail);

  useEffect(() => {
    // Zamčená záložka (znovupřipojená routerem) starý stav tréninku nenačítá — ukáže jen
    // zámek a převzetí si stav načte znovu. [TD-461]
    if (step !== undefined || isLockedByOtherTab) return;

    const sessionData = getSessionFromStorage(userEmail);
    const savedSessionId = sessionData?.sessionId;

    if (
      sessionData === null ||
      typeof savedSessionId !== "string" ||
      savedSessionId.length === 0 ||
      sessionId !== savedSessionId
    ) {
      navigate("/cviceni");
      return;
    }

    dispatch(setGlobalTimer(sessionData.spentTime));
    dispatch(setSavedExerciseAssignments(sessionData));
    dispatch(startGlobalTimer());

    return () => {
      dispatch(stopAllTimers());
    };
  }, []);

  useEffect(() => {
    if (assignments.length === 0 || step !== undefined) return;

    setStep("test");
  }, [assignments]);

  useEffect(() => {
    if (isLockedByOtherTab) dispatch(stopAllTimers());
  }, [isLockedByOtherTab]);

  const onQuit = () => {
    navigate("/");
  };

  const onFinishingLastAssignment = () => {
    setStep("summary");

    changeBgColor("#fff5eb"); // $yellow-100
  };

  const onSummaryClose = () => {
    setStep("results");

    changeBgColor("#fff"); // $white
  };

  const changeBgColor = (color: string) => {
    const root = document.getElementById("root");
    if (!root) return;

    root.style.setProperty("--bg-color", color);
  };

  useEffect(
    () => () => {
      changeBgColor("#fff"); // $white fallback
    },
    [],
  );

  // Úlohy se v zamčené záložce vůbec nepřipojí, takže z ní nejde nic odeslat. Převzetí
  // stránku znovu načte, aby si vzala stav, který mezitím uložila druhá záložka. [TD-461]
  if (isLockedByOtherTab) {
    return (
      <div className="fullpage bg-primary-subtle">
        <div className="text-center px-3 mw-sm">
          <Image src="/images/capy-paused.svg" alt="" className="mb-20px" />
          <h1 className="h2">Trénink pokračuje v jiné záložce</h1>
          <p>
            Trénink se otevřel v jiné záložce prohlížeče. Odpovídat jde jen v jedné záložce, aby se výsledky
            nepomíchaly. Tady na něj můžeš navázat.
          </p>
          <Button
            className="w-100"
            onClick={() => {
              window.location.reload();
            }}
          >
            Pokračovat tady
          </Button>
        </div>
      </div>
    );
  }

  if (step === "test") return <Test onQuit={onQuit} onFinishingLastAssignment={onFinishingLastAssignment} />;
  if (step === "summary") return <Summary onSubmit={onSummaryClose} />;
  if (step === "results") return <Results />;

  return <h1>Something went wrong</h1>;
};

export default Exercise;
